import { ACSES_COMMUNICATION_EVENT_TYPES } from "./AcsesCommunication";
import { AnswerItem } from "./AnswerItem";
import { Appointment, AppointmentID } from "./Appointment";
import { Car, CarID } from "./Car";
import { CarCheckID } from "./CarCheck";
import { CheckInRemark, CheckInResult, CommunicationAgreement } from "./CheckIn";
import { CHECKLIST_TYPES } from "./Checklist";
import { CommunicationSetting } from "./CommunicationSetting";
import { Customer, CustomerID } from "./Customer";
import { COMMUNICATION_EVENTS, CommunicationEvent } from "./CustomerCommunicationEvent";
import { DealerLocationID } from "./DealerLocation";
import { DeskCommunicationID } from "./DeskCheckin";
import { KEYLOCKER_COMMUNICATION_EVENT_TYPE } from "./KeylockerCommunication";
import { LANGUAGE_CODES } from "./Language";
import { PIN_TYPES } from "./Pin";
import { QuestionOption } from "./Question";
import { QUESTION_RESULT_STATUSES, QuestionResultID, QuestionResultImage, QuestionResultVideo } from "./QuestionResult";
import { Tyre, TYRE_POSITIONS, TyreID, TyreReplacement } from "./Tyre";
import { User, UserID } from "./User";

export type CustomerCommunicationID = number;

export type CommunicationMediumID = number;

export type DiagnoseOverviewRemarkID = number;

export type DiagnoseOverviewAttachmentID = number;

export type TwilioMessageSID = string;

export enum COMMUNICATION_STATUSES {
  OnlineCheckInSent = 1,
  OnlineCheckInOpened = 2,
  OnlineCheckInAnswered = 3,
  OnlineCheckInClosed = 4,
  DiagnoseOverviewSent = 5,
  DiagnoseOverviewOpened = 6,
  DiagnoseOverviewAnswered = 7,
  DiagnoseOverviewClosed = 8,
  RepairOverviewSent = 9,
  RepairOverviewOpened = 10
}

export enum COMMUNICATION_CHANNELS {
  Email = 1,
  SMS = 2,
  Webhook = 3,
  WhatsApp = 4
}

export enum COMMUNICATION_RESULT_STATUSES {
  NotAnsweredStatus = 1,
  OkStatus = 2,
  NotOkStatus = 3,
  ContactMeStatus = 4
}

export enum HEADING_POSITIONS {
  HeadingTop = 1,
  HeadingBottom = 2
}

export enum DESK_URL_STATUSES {
  Received = 1,
  FailedToOpen = 2
}

export enum SMS_GATEWAYS {
  Disabled = 1,
  Twilio = 3
}

export interface CustomerCommunication {
  created_on: string;
  updated_on: string;
  customer_communication_id: CustomerCommunicationID; // CustomerCommunication Identifier
  receptionist_key: string; // Key given and used by the Receptionist
  status: COMMUNICATION_STATUSES; // Current status of this CustomerCommunication
  version: number; // Version of Online-CheckIn at the time this was created
  correct_phone: string; // Phone Number given by the Customer
  correct_email: string; // Email given by the Customer
  star_rating_experience_message: string; // Message sent to the customer for the star rating experience
  star_rating_experience: number; // Overall experience rating provided by the customer
  star_rating_service_message: string; // Message sent to the customer for the star rating service
  star_rating_service: number; // Rating provided by the customer for the services
  star_rating_speed_message: string; // Message sent to the customer for the star rating speed
  star_rating_speed: number; // Overall rating provided by the customer to the speed of all the process
  star_rating_feedback: string; // Feedback provided by the customer
  customer_id: CustomerID | null; // Customer who this CustomerCommunication is for
  appointment_id: AppointmentID; // Appointment from which this CustomerCommunication originated from
  car_id: CarID; // Car from which the Appointment is for
  receptionist_id: UserID | null; // Receptionist who last interacted with this CustomerCommunication
  dealer_location_id: DealerLocationID; // DealerLocation from which the Appointment is from
  note: string; // Note set by Receptionist
  customer?: Customer; // Customer who this CustomerCommunication is for
  appointment?: Appointment; // Appointment from which this CustomerCommunication originated from
  car?: Car; // Car from which the Appointment is for
  receptionist?: User; // Receptionist who last interacted with this CustomerCommunication
  check_in_results?: CheckInResult[]; // CheckInResults that the Customer was sent during online or desk or keylocker check-in
  check_in_remarks?: CheckInRemark[]; // CheckInRemarks that the Customer created
  diagnose_overview_results?: CommunicationResult[]; // Diagnose Overview Results that the customer was sent in diagnose overview
  diagnose_overview_remarks?: DiagnoseOverviewRemark[]; // Remarks entered during diagnose overview by the customer
  results?: CommunicationResult[]; // CommunicationResults that the customer was sent in diagnose or repair overview
  agreements?: CommunicationAgreement[]; // CommunicationAgreements that have been agreed
  events?: CommunicationEvent[]; // CommunicationEvents that have occurred
  is_receptionist?: boolean; // The Receptionist is currently interacting with this CustomerCommunication
  settings?: CommunicationSetting; // Settings for this CustomerCommunication
  frontend_update_required: boolean; // When this is true, data has been modified on the contained entities and so the frontend must show this new data to the customer
}

export interface CommunicationResult {
  created_on: string;
  updated_on: string;
  question_result_id: QuestionResultID; // Identifier of the QuestionResult sent in the communication
  customer_communication_id: CustomerCommunicationID; // present for customer communications
  desk_communication_id: DeskCommunicationID; // present for desk communications
  status: COMMUNICATION_RESULT_STATUSES; // present for desk check in/out and diagnose, where it is possible to answer for each question result
  car_check_id?: CarCheckID; // Identifier of the CarCheck the question result belongs to
  checklist_type: CHECKLIST_TYPES; // Type of the checklist the question result belongs to
  checklist_name?: string; // Name of the checklist the question result belongs to
  checklist_order?: number; // Display order of the checklist for the dealer location, 0 if not set
  group_name?: string; // Name of the question group
  group_order?: number; // Display order of the question group within the checklist
  question_order?: number; // Display order of the question within the group
  question_options?: QuestionOption[]; // Options of the question, refer to that model for more details
  title?: string; // Question's title
  price?: number; // Cost for this item
  mechanic_fixed?: boolean; // The mechanic fixed this item
  mechanic_notes?: string; // Mechanic note
  question_status: QUESTION_RESULT_STATUSES; // Status of the question result, refer to that enumeration for more details
  customer_approved?: boolean; // The customer approved to fix this item
  images?: QuestionResultImage[]; // Images taken for this item
  videos?: QuestionResultVideo[]; // Videos taken for this item
  answer_item?: AnswerItem; // Answer item of the question result, if any
  tyre_id?: TyreID; // ID of the linked tyre, null if not linked to a tyre
  tyre_profile?: number; // The profile of the linked tyre, 0 if TyreID is null
  tyre?: Tyre; // The linked tyre
  tyre_position: TYRE_POSITIONS; // Position of the linked tyre, 0 if TyreID is null
  tyre_replacements?: TyreReplacement[]; // Tyre replacements proposed for this item, if any
  pin_type_id?: PIN_TYPES; // Type of the pin attached to the question result, null if not pinned
}

export interface CommunicationMedium {
  created_on: string;
  updated_on: string;
  communication_medium_id: CommunicationMediumID;
  language_code: LANGUAGE_CODES;
  event_type: COMMUNICATION_EVENTS | null;
  keylocker_communication_event_type: KEYLOCKER_COMMUNICATION_EVENT_TYPE | null;
  acses_communication_event_type: ACSES_COMMUNICATION_EVENT_TYPES | null;
  channel: COMMUNICATION_CHANNELS;
  subject: string;
  message: string;
  car_brand: string;
  car_model: string;
  dealer_location_id: DealerLocationID;
}

export interface DiagnoseOverviewRemark {
  created_on: string;
  updated_on: string;
  id: DiagnoseOverviewRemarkID;
  customcom_id: CustomerCommunicationID; // Customer communication ID
  title: string; // Title given by the Customer
  description: string; // Description given by the Customer
  customer_name: string; // Name of the Customer
  receptionist_handled: boolean; // A Receptionist has handled this remark
  visible_to_mechanic: boolean; // Is visible to mechanic
  attachments?: DiagnoseOverviewAttachment[]; // Attachments
  appointment_id?: AppointmentID; // Appointment Identifier
}

export interface DiagnoseOverviewAttachment {
  created_on: string;
  updated_on: string;
  id: DiagnoseOverviewAttachmentID;
  url: string;
  name: string;
  customcom_id: CustomerCommunicationID;
  diagnose_overview_remark_id: DiagnoseOverviewRemarkID | null;
}
